import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaHome } from "react-icons/fa";
import { MdApartment } from "react-icons/md";
import api from '../api';
import { useTr } from '../lib/i18n';
import { ruListPath, RU_BASE_MAP } from '../lib/ru';

// Config: label, path, API endpoint, city slug, sale type, icon.
const RO_LINKS = [
    { label: 'Vânzare', sub: 'apartamente în Bălți', path: '/apartamente/balti', endpoint: 'apartments', city: 'balti', saleType: 1, Icon: MdApartment },
    { label: 'Vânzare', sub: 'apartamente în Ungheni', path: '/apartamente/ungheni', endpoint: 'apartments', city: 'ungheni', saleType: 1, Icon: MdApartment },
    { label: 'Vânzare', sub: 'apartamente în Chișinău', path: '/apartamente/chisinau', endpoint: 'apartments', city: 'chisinau', saleType: 1, Icon: MdApartment },
    { label: 'Chirie', sub: 'apartamente în Bălți', path: '/apartamente/balti/chirie', endpoint: 'apartments', city: 'balti', saleType: 2, Icon: MdApartment },
    { label: 'Chirie', sub: 'apartamente în Ungheni', path: '/apartamente/ungheni/chirie', endpoint: 'apartments', city: 'ungheni', saleType: 2, Icon: MdApartment },
    { label: 'Chirie', sub: 'apartamente în Chișinău', path: '/apartamente/chisinau/chirie', endpoint: 'apartments', city: 'chisinau', saleType: 2, Icon: MdApartment },
    { label: 'Vânzare', sub: 'case în Bălți', path: '/case/balti', endpoint: 'houses', city: 'balti', saleType: 1, Icon: FaHome },
    { label: 'Vânzare', sub: 'case în Ungheni', path: '/case/ungheni', endpoint: 'houses', city: 'ungheni', saleType: 1, Icon: FaHome },
    { label: 'Vânzare', sub: 'case în Chișinău', path: '/case/chisinau', endpoint: 'houses', city: 'chisinau', saleType: 1, Icon: FaHome },
    { label: 'Chirie', sub: 'case în Bălți', path: '/case/balti/chirie', endpoint: 'houses', city: 'balti', saleType: 2, Icon: FaHome },
    { label: 'Chirie', sub: 'case în Ungheni', path: '/case/ungheni/chirie', endpoint: 'houses', city: 'ungheni', saleType: 2, Icon: FaHome },
    { label: 'Chirie', sub: 'case în Chișinău', path: '/case/chisinau/chirie', endpoint: 'houses', city: 'chisinau', saleType: 2, Icon: FaHome },
];

const RU_LINKS = [
    { label: 'Продажа', sub: 'квартир в Унгенах', path: ruListPath('kvartiry', 'ungheni', 1), endpoint: 'apartments', city: 'ungheni', saleType: 1, Icon: MdApartment },
    { label: 'Аренда', sub: 'квартир в Унгенах', path: ruListPath('kvartiry', 'ungheni', 2), endpoint: 'apartments', city: 'ungheni', saleType: 2, Icon: MdApartment },
    { label: 'Продажа', sub: 'домов в Унгенах', path: ruListPath('doma', 'ungheni', 1), endpoint: 'houses', city: 'ungheni', saleType: 1, Icon: FaHome },
    { label: 'Аренда', sub: 'домов в Унгенах', path: ruListPath('doma', 'ungheni', 2), endpoint: 'houses', city: 'ungheni', saleType: 2, Icon: FaHome },
    { label: 'Продажа', sub: 'участков в Унгенах', path: ruListPath('uchastki', 'ungheni', 1), endpoint: 'lands', city: 'ungheni', saleType: 1, Icon: FaHome },
    { label: 'Аренда', sub: 'участков в Унгенах', path: ruListPath('uchastki', 'ungheni', 2), endpoint: 'lands', city: 'ungheni', saleType: 2, Icon: FaHome },
    { label: 'Продажа', sub: 'коммерческой недвижимости', path: ruListPath('kommercheskaya-nedvizhimost', 'ungheni', 1), endpoint: 'commercial-spaces', city: 'ungheni', saleType: 1, Icon: MdApartment },
    { label: 'Аренда', sub: 'коммерческой недвижимости', path: ruListPath('kommercheskaya-nedvizhimost', 'ungheni', 2), endpoint: 'commercial-spaces', city: 'ungheni', saleType: 2, Icon: MdApartment },
];

// Only show links for combos that actually have published offers.
const FastLinks = () => {
    const tr = useTr();
    const lang = tr('ro', 'ru');
    const LINKS = lang === 'ru' ? RU_LINKS : RO_LINKS;
    const [counts, setCounts] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const fetchCounts = async () => {
            try {
                const results = await Promise.all(
                    LINKS.map((link) =>
                        api.get(`/api/v1/${link.endpoint}/`, {
                            params: {
                                location: link.city,
                                sale_type: link.saleType,
                                availability: true,
                                page: 1,
                            },
                        })
                    )
                );
                if (!cancelled) {
                    setCounts(results.map((res) => res.data.count));
                }
            } catch (error) {
                console.error('FastLinks counts failed:', error);
            }
        };
        fetchCounts();
        return () => { cancelled = true; };
    }, [lang]);

    const visible = counts
        ? LINKS.map((link, idx) => ({ ...link, count: counts[idx] })).filter((l) => l.count > 0)
        : null;

    if (visible && visible.length === 0) return null;

    return (
        <section className="pt-[60px] px-[12px] sm:container sm:mx-auto">
            <div className="grid gap-4 md:gap-8 grid-cols-2 md:grid-cols-4 font-body w-full">
                {(visible ?? LINKS).map((link, idx) => {
                    const Icon = link.Icon;
                    return (
                        <Link key={idx} to={link.path} className="bg-[#ececec] rounded-[5px] p-6 text-left hover:shadow-lg transition duration-100 ease-in w-full">
                            <div className="bg-[#d5ab63] w-14 h-14 flex items-center justify-center rounded-full mb-4 mx-auto">
                                <Icon className="text-white text-2xl " />
                            </div>
                            <h4 className="text-[14px] cstmvvv:text-[16px] cstmvvv:font-[500] lg:text-lg lg:font-semibold mb-2 text-center">
                                {link.label}<br />{link.sub}
                            </h4>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
};

export default FastLinks;
